import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import Cookies from "universal-cookie";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import { APPLINK, baseUrl, domainName } from "src/utils/config";

/**
 * The client's session, and what happens when the gateway stops accepting it.
 *
 * The session is one JWT in the `JWToken` cookie, written by the webapp at
 * sign-in on the shared parent domain. There is no separate refresh token:
 * the gateway's `GET /auth/token/refresh` takes the current token as a Bearer,
 * expired or not, checks its signature and that it was issued within the last
 * 24 hours, and answers with a fresh one. Once the cookie itself is gone there
 * is nothing to refresh with, and the only way back is signing in again.
 *
 * Before this, the three kinds of client in the dashboard each did something
 * different with a rejected token. The two axios instances cleared the cookie
 * and sent the browser to the webapp, never trying to refresh; meanwhile the
 * request that failed still rejected, so the page that made it went on to draw
 * its error state — the overview redirected to /error — in the moment before
 * the browser left. The RTK Query APIs did nothing at all: a 401 there was an
 * ordinary failed request, and the reader was left on a page of error
 * messages with a session that would never work again. And with no cookie at
 * all, the app asked for /me anyway and found out from the 401.
 *
 * Every client now goes through the same two steps. A rejected request asks
 * for one refresh, shared by every request that was rejected at the same
 * time, and is sent again once with the new token. If there is no token to
 * refresh, the refresh is refused, or the retried request is rejected too,
 * the session is over: the cookie is cleared and the browser goes to the
 * login page.
 */

export const TOKEN_COOKIE = "JWToken";

/* The webapp's own sign-in page. The dashboard has a /login route of its own,
   but it is the test form; clients sign in on the webapp. The webapp reads no
   return address from the URL, so after signing in a client lands on the
   overview rather than on the page they were sent away from. */
export const LOGIN_URL = `${APPLINK}/login`;

const REFRESH_URL = `${baseUrl}/auth/token/refresh`;

/* The same lifetime the webapp gives the cookie at sign-in. The token inside
   may outlive it; when the cookie goes, so does the session. */
const TOKEN_MAX_AGE = 3600 * 2;

const cookieOptions = () => ({
  domain: domainName,
  path: "/",
  // A Secure cookie is not written over plain http, which is how the
  // dashboard runs on a developer's machine.
  secure: window.location.protocol === "https:",
});

export const getToken = (): string | undefined => {
  const token = new Cookies().get(TOKEN_COOKIE);
  return token ? String(token) : undefined;
};

const setToken = (token: string) => {
  new Cookies().set(TOKEN_COOKIE, token, {
    ...cookieOptions(),
    maxAge: TOKEN_MAX_AGE,
  });
};

/**
 * Whether a rejection is about the session rather than the request.
 *
 * Most 401s from the gateway mean "I do not know who you are": no
 * Authorization header (AUTH_HEADER_ERROR), a token that does not verify
 * (`{ err: { message: "jwt expired" } }`), "Unauthorized" from the client
 * checks, "Invalid token" from the error handler. A few are about the request
 * itself and name what was wrong with it in an `errorCode` — OTP_CODE_ERROR
 * for a mistyped code is the one the dashboard meets. Refreshing the token
 * does nothing for a wrong code, and sending the same code again would only
 * fail the same way and then sign the client out, so those reach the screen
 * that asked, the way any other refusal does.
 */
export const isSessionRejection = (status: unknown, body: unknown) => {
  if (status !== 401) return false;
  const errorCode = (body as { errorCode?: unknown } | null)?.errorCode;
  return typeof errorCode !== "string" || errorCode === "AUTH_HEADER_ERROR";
};

/* -------------------------------------------------------------------------
 * Ending the session
 */

let ending = false;
const listeners = new Set<() => void>();

export const isSessionEnding = () => ending;

/** Called once the session ends, so the app can stop drawing pages. */
export const onSessionEnd = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Clears the session and sends the browser to sign in.
 *
 * Only the first call does anything: when a token is refused, every request
 * in flight is refused with it, and each would otherwise start its own
 * redirect. `replace` rather than `assign`, so Back does not return to a page
 * that would only send the client here again.
 *
 * The cookie is cleared before leaving, and that is what keeps this from
 * looping: the webapp sends anyone who arrives at its login page with a token
 * straight back to the dashboard.
 */
export const endSession = () => {
  if (ending) return;
  ending = true;

  const cookies = new Cookies();
  cookies.remove(TOKEN_COOKIE, cookieOptions());
  cookies.remove("_expiredTime", cookieOptions());
  try {
    localStorage.removeItem("analytics");
  } catch (error) {
    // Storage can be unavailable; the cookie is what holds the session.
  }

  listeners.forEach((listener) => listener());

  if (window.location.href.startsWith(LOGIN_URL)) return;
  window.location.replace(LOGIN_URL);
};

/**
 * What a request that ended the session resolves to: nothing, ever.
 *
 * Rejecting would hand the failure to whichever screen made the request, and
 * screens turn failures into error pages and toasts — exactly what the client
 * should not see on the way to the login page. The browser is already leaving,
 * so the request is simply never answered, and the screen keeps showing that
 * it is loading until the page goes.
 */
export const leftUnanswered = <T = never>(): Promise<T> =>
  new Promise<T>(() => undefined);

/* -------------------------------------------------------------------------
 * Refreshing the session
 */

let renewal: Promise<string | null> | null = null;

/* Plain axios rather than the app's instances, so the refresh's own 401 never
   comes back through the interceptors below and asks for another refresh. */
const requestNewToken = async (current: string) => {
  try {
    const response = await axios.get(REFRESH_URL, {
      headers: { Authorization: `Bearer ${current}` },
      timeout: 30000,
    });
    const token = response?.data?.token;
    if (typeof token !== "string" || !token) return null;
    setToken(token);
    return token;
  } catch (error) {
    return null;
  }
};

/**
 * A token to send again with, or null when the session cannot be renewed.
 *
 * `sentWith` is the token the refused request carried. If the cookie already
 * holds a different one, another request has refreshed the session since this
 * one went out — its refusal arrived late — and the new token is the answer
 * without asking again. Requests refused while a refresh is under way wait on
 * that same refresh.
 */
export const renewSession = (
  sentWith?: string | null,
): Promise<string | null> => {
  if (ending) return Promise.resolve(null);
  if (renewal) return renewal;

  const current = getToken();
  if (!current) return Promise.resolve(null);
  if (sentWith !== undefined && current !== sentWith)
    return Promise.resolve(current);

  renewal = requestNewToken(current).finally(() => {
    renewal = null;
  });
  return renewal;
};

/* -------------------------------------------------------------------------
 * The clients
 */

type RetriableConfig = AxiosRequestConfig & { _sessionRetried?: boolean };

const bearerOf = (config: AxiosRequestConfig) => {
  const header =
    config?.headers?.Authorization ?? config?.headers?.authorization;
  return typeof header === "string" ? header.replace(/^Bearer\s+/i, "") : null;
};

/**
 * Teaches an axios instance to renew the session when a request is refused.
 *
 * The retry goes back through the instance, whose request interceptor reads
 * the token from the cookie again, so it carries the new one.
 */
export const renewSessionOn401 = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error?.config as RetriableConfig | undefined;
      const response = error?.response;
      if (!config || !isSessionRejection(response?.status, response?.data))
        return Promise.reject(error);

      if (ending) return leftUnanswered();

      // Refused again with a token the gateway has only just issued: the
      // session is not coming back, and asking once more would loop.
      if (config._sessionRetried) {
        endSession();
        return leftUnanswered();
      }

      const token = await renewSession(bearerOf(config));
      if (!token) {
        endSession();
        return leftUnanswered();
      }

      config._sessionRetried = true;
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
      return instance.request(config);
    },
  );
  return instance;
};

/**
 * The same for an RTK Query base query: refused → renew once → send again.
 *
 * `prepareHeaders` reads the cookie on every call, so the second call carries
 * the new token without being told it.
 */
export const renewSessionInBaseQuery =
  <
    Args,
    Result,
    Failure extends { status?: unknown; data?: unknown },
    Extra,
    Meta,
  >(
    baseQuery: BaseQueryFn<Args, Result, Failure, Extra, Meta>,
  ): BaseQueryFn<Args, Result, Failure, Extra, Meta> =>
  async (args, api, extraOptions) => {
    const sentWith = getToken() ?? null;
    const result = await baseQuery(args, api, extraOptions);
    if (!isSessionRejection(result.error?.status, result.error?.data))
      return result;

    if (ending) return leftUnanswered();

    const token = await renewSession(sentWith);
    if (!token) {
      endSession();
      return leftUnanswered();
    }

    const retried = await baseQuery(args, api, extraOptions);
    if (isSessionRejection(retried.error?.status, retried.error?.data)) {
      endSession();
      return leftUnanswered();
    }
    return retried;
  };
